import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../styles/theme';

/**
 * CrewContextChips — Sprint 4 / FE-10.8 + FE-10.9.
 *
 * Renders a row of context badges on a BorrowRequestDetails-shaped
 * row. Surfaces:
 *   - 👥 {group_name}        when group_id is set (in-crew request)
 *   - 🚨 Recall              when priority === 'RECALL'
 *   - ⚠ Urgent               when priority === 'URGENT'
 *   - Waitlist #N            when queue_position > 1
 *
 * Returns null for vanilla marketplace rows (no crew, NORMAL
 * priority, no queue position) so the existing card layout doesn't
 * grow an empty chip strip.
 */

interface CrewContextChipsProps {
  /** Loose shape — BorrowRequestDetails or any equivalent row.
   *  Defensive against fields that may be null/undefined on
   *  pre-augmentation rows. */
  item: {
    group_id?: number | null;
    group_name?: string | null;
    priority?: string | null;
    queue_position?: number | null;
  };
  /** FE-228 — when 'sent', the waitlist chip reads in first-person
   *  ("You're #2 in line") instead of the neutral "Waitlist #N". The
   *  default 'received' POV stays generic so the owner sees the queue
   *  position without first-person framing. */
  mode?: 'received' | 'sent';
}

export const CrewContextChips: React.FC<CrewContextChipsProps> = ({
  item,
  mode = 'received',
}) => {
  const chips: React.ReactNode[] = [];

  if (item.group_id != null && item.group_name) {
    chips.push(
      <View key="crew" style={[styles.chip, styles.crew]}>
        <MaterialIcons name="group" size={11} color={fpColors.primary} />
        <Text style={[styles.text, { color: fpColors.primary }]} numberOfLines={1}>
          {item.group_name}
        </Text>
      </View>,
    );
  }

  if (item.priority === 'RECALL') {
    chips.push(
      <View key="recall" style={[styles.chip, styles.recall]}>
        <MaterialIcons name="priority-high" size={11} color="#FFFFFF" />
        <Text style={[styles.text, { color: '#FFFFFF' }]}>Recall</Text>
      </View>,
    );
  } else if (item.priority === 'URGENT') {
    chips.push(
      <View key="urgent" style={[styles.chip, styles.urgent]}>
        <MaterialIcons name="bolt" size={11} color="#FFFFFF" />
        <Text style={[styles.text, { color: '#FFFFFF' }]}>Urgent</Text>
      </View>,
    );
  }

  if (typeof item.queue_position === 'number' && item.queue_position > 1) {
    // FE-228 — first-person on sent side ("You're #2 in line"), neutral
    // on received ("Waitlist #2") because the owner is seeing the queue
    // from outside, not as a participant.
    const label =
      mode === 'sent'
        ? `You're #${item.queue_position} in line`
        : `Waitlist #${item.queue_position}`;
    chips.push(
      <View key="queue" style={[styles.chip, styles.queue]}>
        <MaterialIcons name="queue" size={11} color={fpColors.outline} />
        <Text style={[styles.text, { color: fpColors.outline }]}>{label}</Text>
      </View>,
    );
  }

  if (chips.length === 0) return null;
  return <View style={styles.row}>{chips}</View>;
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  text: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    maxWidth: 140,
  },
  crew: {
    backgroundColor: `${fpColors.primary}1a`, // primary @ 10%
  },
  recall: {
    backgroundColor: fpColors.error,
  },
  urgent: {
    backgroundColor: fpColors.tertiary,
  },
  queue: {
    backgroundColor: fpColors.surfaceContainer,
  },
});

export default CrewContextChips;
